import type { Point } from '@racemap/utilities/types/types';
import { type KeyboardEvent, type MouseEvent, useRef, useState } from 'react';
import TrackPointsImport from '../models/TrackPointsImport';
import { DropZone } from './DropZone';
import { IconFail, IconSpinner, IconTrash, IconUpload } from './Icon';

enum STATUS {
  PROCESSING = 'PROCESSING',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

type IndicatorProps = {
  status: STATUS | null;
};

function StatusIndicator({ status }: IndicatorProps) {
  switch (status) {
    case STATUS.PROCESSING:
      return (
        <div>
          <IconSpinner /> Processing
        </div>
      );
    case STATUS.FAILURE:
      return (
        <div>
          <IconFail /> Failed
        </div>
      );
    default:
      return <div />;
  }
}

type Track = {
  points: Array<Point>;
  name: string;
};

type Props = {
  onChange: (arg0: Track | null) => void;
  value: Track | null;
  id: string;
};

function TrackDropView(props: Props) {
  const fileInputRef = useRef<null | HTMLInputElement>();
  const [status, setStatus] = useState<STATUS | null>(null);

  async function handleFilesDrop(files: FileList) {
    if (files.length > 0) {
      const file = files[0];
      setStatus(STATUS.PROCESSING);
      try {
        const points = await TrackPointsImport.importFile(file);
        props.onChange({ points, name: file.name });
        setStatus(STATUS.SUCCESS);
      } catch {
        setStatus(STATUS.FAILURE);
      }
    }
  }

  function handleDropTargetClick() {
    const inputField = fileInputRef.current;
    if (inputField != null) {
      inputField.click();
    }
  }

  function handleRemoveClick(
    event: MouseEvent<HTMLLinkElement, MouseEvent> | KeyboardEvent<SVGSVGElement>,
  ) {
    event.stopPropagation();
    props.onChange(null);
  }

  return (
    <DropZone
      className="track-dropview"
      onFilesDrop={handleFilesDrop}
      disabled={false}
      showOverlay={false}
      allowedItemKind="file"
    >
      <div
        className="drop-target"
        onClick={handleDropTargetClick}
        onKeyUp={(e) => e.key === 'Enter' && handleDropTargetClick()}
      >
        {props.value != null ? (
          <div>
            <div>
              {props.value.name} ({props.value.points.length} points){' '}
              <IconTrash
                className="pointer remove-track"
                onClick={(e) => handleRemoveClick(e)}
                onKeyUp={(e) => e.key === 'Enter' && handleRemoveClick(e)}
              />
            </div>
          </div>
        ) : (
          <div>
            <IconUpload className="upload-button" />
            <input
              type="file"
              className="upload-image"
              multiple={false}
              style={{ display: 'none' }}
              id={props.id}
              ref={fileInputRef}
              onChange={(e) => handleFilesDrop(e.target.files)}
            />
          </div>
        )}
      </div>
      <StatusIndicator status={status} />
    </DropZone>
  );
}

export default TrackDropView;
